import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_BG_IMAGE = "https://customer-assets.emergentagent.com/job_biz-directory-27/artifacts/miycx0rf_614shoplocal-background.png";

const LogoUpload = ({ 
  currentLogo, 
  businessName, 
  onUploadSuccess, 
  uploadEndpoint,
  token 
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Clear preview when currentLogo changes (after successful upload)
  useEffect(() => {
    if (currentLogo) {
      setPreviewUrl(null);
    }
  }, [currentLogo]);

  const getLogoUrl = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('http') || logo.startsWith('data:')) return logo;
    return `${BACKEND_URL}${logo}`;
  };

  const logoUrl = getLogoUrl(currentLogo);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      alert("Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.");
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${BACKEND_URL}${uploadEndpoint}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const data = await response.json();
      if (onUploadSuccess) {
        onUploadSuccess(data.logo_url);
      }
      // Keep preview until the new logo URL is set from parent
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload logo. Please try again.");
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    if (onUploadSuccess) {
      onUploadSuccess("");
    }
    setPreviewUrl(null);
  };

  const displayUrl = previewUrl || logoUrl;

  return (
    <div className="space-y-3">
      {/* Preview Container - 465x300 aspect ratio */}
      <div 
        className="relative flex items-center justify-center border border-slate-200 rounded-lg overflow-hidden"
        style={{ 
          width: '100%',
          maxWidth: '465px',
          height: '0',
          paddingBottom: '64.5%', // 300/465
          backgroundImage: `url(${DEFAULT_BG_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {displayUrl ? (
          <div className="absolute inset-0 flex items-center justify-center p-6 bg-white">
            <img
              src={displayUrl}
              alt="Logo preview"
              className="max-h-full max-w-full object-contain"
              style={{ maxHeight: '80%', maxWidth: '80%' }}
            />
            {!uploading && (
              <button
                onClick={handleRemoveLogo}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <span 
              className="text-lg md:text-xl font-bold text-center text-slate-800"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {businessName || "Your Business Name"}
            </span>
          </div>
        )}
        
        {uploading && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
          </div>
        )}
      </div>

      {/* Upload Button */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />
      <Button
        type="button"
        variant="outline"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="w-full"
      >
        <Upload className="w-4 h-4 mr-2" />
        {uploading ? "Uploading..." : displayUrl ? "Change Logo" : "Upload Logo"}
      </Button>
      <p className="text-xs text-slate-500 text-center">
        Recommended: Square logo, PNG or JPG, max 2MB
      </p>
    </div>
  );
};

export default LogoUpload;
